import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import RoboticMowerAPI, { SendCommandResponse } from '~/api/robotic-mower-api';
import * as roboticMowerCommandActions from '~/ducks/robotic-mower-command/actions';
import RoboticMowerCommand from '~/ducks/robotic-mower-command/robotic-mower-command';
import type RoboticMowerSetting from '~/ducks/robotic-mower-command/robotic-mower-setting';
import callRequestSaga from '~/sagas/utils/request-saga';
import BaseException from '~/utils/fetch-wrapper/errors/BaseException';
import * as roboticMowerLoadingStateActions from '../robotic-mower-loading-states/reducer';
import * as roboticMowerCommandErrorsActions from '../robotic-mower-command-errors/reducer';

export function* handleMachineCommandErrorResponse(
  response: SendCommandResponse,
  commandName: RoboticMowerCommand | RoboticMowerSetting
) {
  for (const machineResult of response) {
    if (!machineResult.success) {
      yield put(
        roboticMowerCommandErrorsActions.addErrorCommand({
          commandName,
          assetId: machineResult.iprId,
          missionId: machineResult.missionId,
        })
      );
    }
  }
}

export function* updateAssetStatuses(
  response: SendCommandResponse,
  command: RoboticMowerCommand | RoboticMowerSetting,
  loadingState: boolean,
  successAction: (arg: { loadingState: boolean; assetId: AssetId; missionId: number | null }) => any
) {
  const failed = response.filter((r) => !r.success);
  const successful = response.filter((r) => r.success);

  yield call(handleMachineCommandErrorResponse, failed, command);

  if (successful.length === 0) {
    return;
  }

  for (const row of successful) {
    yield put(
      roboticMowerCommandErrorsActions.resetErrorCommand({
        assetId: row.iprId,
        commandName: command,
      })
    );

    if (!row.followupNotRequired) {
      yield put(
        successAction({
          loadingState,
          assetId: row.iprId,
          missionId: row.missionId,
        })
      );
    }
  }
}

export function* parkRoboticMowersWithCommand({
  payload,
}: ReturnType<typeof roboticMowerCommandActions.parkRoboticMowersWithCommand>) {
  const response = yield* callRequestSaga(
    RoboticMowerAPI.sendCommandToRoboticMowers,
    {
      ...roboticMowerCommandActions.parkRoboticMowersWithCommand.actions,
      payload: {
        ...payload,
        commandType: RoboticMowerCommand.PARK,
      },
    },
    payload.assetIds,
    RoboticMowerCommand.PARK,
    payload.commandActionType,
    null,
    payload.commandTimerInMinutes
  );

  if (response instanceof BaseException) {
    return;
  }

  yield call(
    updateAssetStatuses,
    response,
    RoboticMowerCommand.PARK,
    true,
    roboticMowerLoadingStateActions.setIsParking
  );
}

export function* startRoboticMowersWithCommand({
  payload,
}: ReturnType<typeof roboticMowerCommandActions.startRoboticMowersWithCommand>) {
  const response = yield* callRequestSaga(
    RoboticMowerAPI.sendCommandToRoboticMowers,
    {
      ...roboticMowerCommandActions.startRoboticMowersWithCommand.actions,
      payload: {
        ...payload,
        commandType: RoboticMowerCommand.START,
      },
    },
    payload.assetIds,
    RoboticMowerCommand.START,
    payload.commandActionType,
    payload.missionId,
    payload.commandTimerInMinutes
  );

  if (response instanceof BaseException) {
    return;
  }

  yield call(
    updateAssetStatuses,
    response,
    RoboticMowerCommand.START,
    true,
    roboticMowerLoadingStateActions.setIsStarting
  );
}

export function* parkRoboticMowersWithCommandSaga() {
  yield takeLatest(
    roboticMowerCommandActions.parkRoboticMowersWithCommand.type,
    parkRoboticMowersWithCommand
  );
}

export function* startRoboticMowersWithCommandSaga() {
  yield takeLatest(
    roboticMowerCommandActions.startRoboticMowersWithCommand.type,
    startRoboticMowersWithCommand
  );
}

function* updateAssetStatusesSaga() {
  yield takeEvery(roboticMowerCommandActions.updateAssetStatuses, (action) => {
    return updateAssetStatuses(
      action.payload.response,
      action.payload.command,
      action.payload.loadingState,
      action.payload.successAction
    );
  });
}

export default [
  parkRoboticMowersWithCommandSaga,
  startRoboticMowersWithCommandSaga,
  updateAssetStatusesSaga,
];
